(ns hai.tasks.deadline-picker
  "A task's deadline as [[hai.tasks.form]] edits it: the [[chip]] that shows
   it, whose day and time are two targets, and the picker they open — on the
   day's calendar or the time's box ([[hai.ui.components.date-picker]]). The
   picker holds its own draft until it closes, and is drawn two ways:
   as a [[sheet]] rising over the card the form fills, or as a [[popover]]
   hanging off the chip. Both open from the same [[controls]]."
  (:require
   [clojure.string :as str]
   [garden.stylesheet]
   [hai.tasks.deadline :as deadline]
   [hai.ui.components :as c]
   [hai.ui.components.date-picker :as date-picker]
   [hai.ui.components.datetime :as datetime]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private arrow-icon (icons/mui-icon :outlined :east))
(def ^:private info-icon (icons/mui-icon :outlined :info))

(defn controls
  "Which view the picker is open on — `:date`, `:time`, or nil while it is
   shut — shared by the [[chip]] and the [[sheet]] or [[popover]]."
  []
  {:!open (r/atom nil)})

;; ---------------------------------------------------------------------------
;; The chip
;; ---------------------------------------------------------------------------

;; Tinted by how near the deadline is, as a row's due chip is; with no
;; deadline, the dashed invitation an unset urgency is, on a lighter shade of
;; My Progress's sky. Every state keeps the same 1px border, so none is a
;; different size.
(o/defstyled chip-frame :span
  {:display "inline-flex" :align-items "stretch"
   :align-self "center"
   :box-sizing "border-box"
   :height "26px"
   :overflow "hidden"
   :border-width "1px" :border-style "solid" :border-color "transparent"
   :border-radius "7px"
   :font-size "12px" :font-weight "700" :line-height "1"
   :white-space "nowrap"}
  [:&.overdue {:background t/--card-error-12 :color t/--card-error}]
  [:&.soon {:background t/--card-warning-12 :color t/--card-warning-text}]
  [:&.ahead {:background t/--card-info-12 :color t/--card-info}]
  [:&.unset
   {:border-style "dashed" :border-color "rgba(50,64,37,0.3)"
    :background t/--progress-light
    :color "rgba(74,93,63,0.82)"
    :font-weight "600"}]
  [:.chip-part
   {:display "inline-flex" :align-items "center" :gap "5px"
    :padding "0 9px"
    :border "none" :background "transparent"
    :font "inherit" :color "inherit"
    :cursor "pointer"}
   ;; Stroke icons: the page applies `svg {fill: currentColor}`, which would
   ;; fill the calendar solid.
   [:svg {:flex "none" :fill "none" :opacity "0.8"}]
   [:&:disabled {:cursor "default"}]]
  ;; The seam between the day and the time.
  [:.chip-part+.chip-part {:box-shadow "inset 1px 0 0 rgba(0,0,0,0.12)"}]
  [:at-media {:hover "hover"}
   [".chip-part:not(:disabled):hover" {:background "rgba(255,255,255,0.55)"}]
   [:&.unset
    [".chip-part:not(:disabled):hover" {:background "rgba(50,64,37,0.06)"}]]])

(defn- day-word [date]
  (str/capitalize (datetime/relative-label (datetime/value->moment {:date date}))))

(defn chip
  "`deadline` as a chip: its day and its time as two targets, each opening the
   picker on its own view through `controls` — Add a date alone while there is
   no deadline, Add a time beside a day without one. Without `controls` it
   shows the deadline and nothing to press."
  [{:keys [deadline controls]}]
  (let [{:keys [date time]} deadline
        part (fn [kind icon label title]
               [:button.chip-part
                {:type "button"
                 :title (when controls title)
                 :disabled (nil? controls)
                 ;; A focused title or description editor blurs on mousedown
                 ;; and reflows the form, which would move the chip out from
                 ;; under the click.
                 :on-mouse-down #(.preventDefault %)
                 ;; Opens on the part pressed rather than toggling, so a
                 ;; popover wrapped round the chip doesn't close on it.
                 :on-click (fn [e]
                             (.preventDefault e)
                             (reset! (:!open controls) kind))}
                icon
                [:span label]])]
    [chip-frame {:class (if date
                          (some-> deadline deadline/value->params deadline/closeness name)
                          "unset")}
     (if date
       [:<>
        (part :date [icons/calendar {:size 13}] (day-word date) "Change the date")
        (when (or time controls)
          (part :time [datetime/clock-icon]
                (if time (date-picker/format-minutes time) "Add a time")
                (if time "Change the time" "Add a time")))]
       (part :date [icons/calendar {:size 13}] "Add a date" "Add a date"))]))

;; ---------------------------------------------------------------------------
;; The picker
;; ---------------------------------------------------------------------------

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules picker-keyframes
  (garden.stylesheet/at-keyframes
   :deadline-draft-ring
   ["0%" {:box-shadow "0 0 0 0 rgba(156,174,133,0)"}]
   ["25%" {:box-shadow "0 0 0 3px rgba(156,174,133,0.55)"}]
   ["65%" {:box-shadow "0 0 0 3px rgba(156,174,133,0.55)"}]
   ["100%" {:box-shadow "0 0 0 0 rgba(156,174,133,0)"}])
  (garden.stylesheet/at-keyframes
   :deadline-sheet-in
   ["0%" {:transform "translateY(100%)"}]
   ["100%" {:transform "none"}])
  (garden.stylesheet/at-keyframes
   :deadline-scrim-in
   ["0%" {:opacity "0"}]
   ["100%" {:opacity "1"}]))

(o/defstyled picker-frame :div
  {:display "flex" :flex-direction "column"
   :background t/--card-surface
   :color t/--card-forest
   :outline "none"}
  [:.picker-head
   {:padding "12px 14px"
    :border-bottom-width "1px" :border-bottom-style "solid"
    :border-bottom-color t/--property-3}]
  ;; Labels over values, the arrow between the values: it centres on them
  ;; whatever height the labels take.
  [:.head-grid
   {:display "grid"
    :grid-template-columns "auto auto minmax(0, 1fr)"
    :grid-template-areas "\"was-label . now-label\" \"was arrow now\""
    :column-gap "9px" :row-gap "5px"
    :align-items "center"}]
  [:.head-eyebrow
   {:font-size "10px" :font-weight "800"
    :letter-spacing "0.06em" :text-transform "uppercase"
    :color t/--link-muted}
   [:&.was {:grid-area "was-label"}]
   [:&.now {:grid-area "now-label"}]]
  [:.was-value
   {:grid-area "was"
    :min-height "46px"
    :display "flex" :flex-direction "column" :justify-content "center"}
   [:b {:font-size "12.5px" :font-weight "700" :white-space "nowrap"}]
   [:i {:font-size "11.5px" :font-weight "500" :font-style "normal"
        :color t/--link-muted :white-space "nowrap"}]]
  [:.head-arrow
   {:grid-area "arrow"
    :display "flex" :align-items "center"
    :color t/--property-3}
   [:svg {:width "17px" :height "17px" :fill "currentColor"}]
   [:&.on {:color t/--note-sage}]]
  ;; One control with two halves, not two chips. The lit half is the view the
  ;; picker shows, so pressing the other half is how you switch.
  [:.draft-control
   {:grid-area "now"
    :display "flex" :align-items "stretch"
    :min-height "46px"
    :overflow "hidden"
    :border-width "1.5px" :border-style "solid" :border-color t/--property-3
    :border-radius "12px"
    :background t/--card-forest-06
    :box-shadow "0 1px 2px rgba(45,62,42,0.06)"
    :transition "border-color 0.13s"}
   [:&.doomed {:border-color t/--card-forest}]
   [:&.flash {:animation "deadline-draft-ring 1.5s ease 2"}]]
  ;; Split evenly, so a label changing length never moves the other half.
  [:.half
   {:display "flex" :flex "1 1 0" :width "50%" :min-width "0"}]
  [:.half-inner
   {:display "flex" :align-items "stretch"
    :flex "1 1 auto" :min-width "0"
    :transition "background 0.13s, box-shadow 0.13s"}]
  ;; Each half takes the control's inner corners on its own side, so a ring
  ;; drawn on it follows them.
  [:.half:first-child [:.half-inner {:border-radius "10.5px 0 0 10.5px"}]]
  [:.half:last-child [:.half-inner {:border-radius "0 10.5px 10.5px 0"}]]
  [:.half-pick
   {:display "inline-flex" :align-items "center" :gap "6px"
    :flex "1" :min-width "0"
    :padding "0 12px"
    :border "none" :background "transparent"
    :font-family "inherit" :font-size "13px" :font-weight "500"
    :color t/--link-muted
    :white-space "nowrap"
    :cursor "pointer"
    :transition "color 0.13s"}
   [:svg {:flex "none" :fill "none" :color t/--link-muted}]]
  [:.half-label {:overflow "hidden" :text-overflow "ellipsis"}]
  [:.half.has
   [:.half-pick {:padding "0 0 0 9px" :font-weight "700" :color t/--card-forest}
    [:svg {:color t/--card-forest}]]]
  ;; The lit half is the Add a date chip's pale sky, ringed in My Progress's.
  [:.half.active
   [:.half-inner {:background t/--progress-light
                  :box-shadow "inset 0 0 0 2px var(--progress)"}]
   [:.half-pick [:svg {:color t/--note-sage}]]]
  ;; Hovering a ✕ shows what it would take with it.
  [:.half.doomed
   [:.half-inner {:background t/--property-3}]
   [:.half-pick {:color t/--link-muted :text-decoration "line-through"}
    [:svg {:color t/--link-muted}]]]
  [:.half-clear
   {:flex "none"
    :width "22px" :height "22px"
    :align-self "center"
    :margin "0 6px 0 2px" :padding "0"
    :display "flex" :align-items "center" :justify-content "center"
    :border-width "1.5px" :border-style "solid" :border-color t/--property-3
    :border-radius "7px"
    ;; A see-through tint, so it sits a shade darker on whatever its half is
    ;; lit in: the sky, the sage, or neither.
    :background t/--card-forest-10
    :color t/--link-muted
    :cursor "pointer"
    :transition "border-color 0.13s, color 0.13s"}
   [:svg {:width "11px" :height "11px" :fill "currentColor"}]
   [#{:&:hover :&:focus-visible} {:border-color t/--card-forest :color t/--card-forest}]]
  [:.half-divider {:flex "none" :width "1px" :background t/--property-3}]
  [:.head-note
   {:display "flex" :align-items "center" :gap "6px"
    :margin-top "8px"
    :font-size "11.5px" :font-weight "500"}
   [:svg {:flex "none" :width "13px" :height "13px" :fill t/--note-sage}]]
  ;; Its height follows the view in it with a glide ([[stage]]); the bottom
  ;; padding leaves the view's shadow unclipped.
  [:.picker-stage
   {:overflow "hidden"
    :transition "height 0.24s ease"}]
  [:.picker-stage-content {:padding "12px 14px 14px"}])

(defn- with-date
  "The deadline on `date`; with no day there is no deadline, and so no time."
  [deadline date]
  (when date
    (assoc deadline :date date)))

(defn- with-time
  "The deadline at `minutes`, or without a time for nil. A time needs a day,
   so one set with none lands today."
  [deadline minutes]
  (cond
    (nil? minutes) (some-> deadline (dissoc :time))
    (:date deadline) (assoc deadline :time minutes)
    :else {:date (date-picker/today-date) :time minutes}))

(defn- stage
  "`view` in a box whose height follows the view's with a glide, so swapping
   the calendar for the shorter time box moves the picker's top edge smoothly
   rather than in one jump. The first height is set as it is, not glided to."
  [view]
  (r/with-let [!observer (atom nil)
               follow! (fn [content]
                         (some-> @!observer .disconnect)
                         (reset! !observer nil)
                         (when content
                           (let [box (.-parentElement content)
                                 fit! #(set! (.. box -style -height)
                                             (str (.-offsetHeight content) "px"))
                                 observer (js/ResizeObserver. fit!)]
                             (fit!)
                             (.observe observer content)
                             (reset! !observer observer))))]
    [:div.picker-stage
     [:div.picker-stage-content {:ref follow!} view]]
    (finally
      (some-> @!observer .disconnect))))

(defn- draft-half
  "One half of the draft control: `kind`'s icon and `label`, and a ✕ while it
   `has?` a value. Hovering or focusing the ✕ tells `on-doom`, so the halves it
   would clear can say so."
  [{:keys [kind label has? active? doomed? on-press on-clear on-doom]}]
  (let [clear-label (if (= :date kind)
                      "Remove the deadline (clears the time too)"
                      "Remove the time")]
    [:div.half {:class (cond-> []
                         has? (conj "has")
                         active? (conj "active")
                         doomed? (conj "doomed"))}
     [:span.half-inner
      [:button.half-pick {:type "button"
                          :aria-pressed active?
                          :on-click on-press}
       (if (= :date kind)
         [icons/calendar {:size 15}]
         [datetime/clock-icon])
       [:span.half-label label]]
      (when has?
        [:button.half-clear {:type "button"
                             :title clear-label
                             :aria-label clear-label
                             :on-click on-clear
                             :on-pointer-enter #(on-doom kind)
                             :on-pointer-leave #(on-doom nil)
                             :on-focus #(on-doom kind)
                             :on-blur #(on-doom nil)}
         [icons/outlined-close]])]]))

(defn- picker
  "The picker on `deadline`, open on the `part` view: what the deadline is
   now, what it will change to — one control with a half for the day and one
   for the time, each clearing with its ✕ — and the day's calendar or the
   time's box beneath. It holds a draft of its own, handed to `on-save` (nil
   for no deadline) when it closes having changed: on a day picked on the
   calendar, on Enter, or when it is closed from outside — pressing off it.
   Escape throws the draft away through `on-cancel`."
  [{:keys [deadline part on-save on-cancel]}]
  (r/with-let [!draft (r/atom deadline)
               !done? (atom false)
               !part (r/atom part)
               !doomed (r/atom nil)
               !filled-today? (r/atom false)
               !flash? (r/atom false)
               !flash-timer (atom nil)
               ;; The time box takes the caret itself; on the calendar, the
               ;; picker holds the focus so Escape and Enter reach it.
               focus-picker! (fn [el]
                               (when (and el (= :date part))
                                 (.focus el #js {:preventScroll true})))]
    (let [{:keys [date time] :as draft} @!draft
          current-part @!part
          doomed @!doomed
          changed? (not= draft deadline)
          finish! (fn []
                    (reset! !done? true)
                    (if (not= @!draft deadline)
                      (on-save @!draft)
                      (on-cancel)))
          cancel! (fn []
                    (reset! !done? true)
                    (on-cancel))
          pick-date! (fn [picked]
                       (reset! !filled-today? false)
                       (reset! !flash? false)
                       (swap! !draft with-date picked))
          set-time! (fn [minutes]
                      (let [fills-today? (boolean (and minutes (nil? (:date @!draft))))]
                        (swap! !draft with-time minutes)
                        (reset! !filled-today? fills-today?)
                        (reset! !flash? fills-today?)
                        (when fills-today?
                          (js/clearTimeout @!flash-timer)
                          (reset! !flash-timer (js/setTimeout #(reset! !flash? false) 3200)))))
          clear! (fn [kind]
                   (reset! !doomed nil)
                   (reset! !part kind)
                   (if (= :date kind)
                     (pick-date! nil)
                     (set-time! nil)))]
      [picker-frame
       {:tab-index -1
        :ref focus-picker!
        :on-key-down (fn [e]
                       (case (.-key e)
                         "Escape" (do (.preventDefault e)
                                      (.stopPropagation e)
                                      (cancel!))
                         "Enter" (when (and changed?
                                            (or (= (.-target e) (.-currentTarget e))
                                                (.. e -target (matches "input"))))
                                   (.preventDefault e)
                                   (.stopPropagation e)
                                   (finish!))
                         nil))}
       [:div.picker-head
        [:div.head-grid
         [:span.head-eyebrow.was "Currently"]
         [:span.head-eyebrow.now "Change to"]
         [:span.was-value
          [:b (if-let [was-date (:date deadline)]
                (date-picker/day-label was-date)
                "No deadline")]
          (when (:date deadline)
            [:i (if-let [was-time (:time deadline)]
                  (date-picker/format-minutes was-time)
                  "No time")])]
         [:span.head-arrow {:class (when changed? "on")} [arrow-icon]]
         [:div.draft-control {:class (cond-> []
                                       (= :date doomed) (conj "doomed")
                                       @!flash? (conj "flash"))}
          [draft-half {:kind :date
                       :label (if date (date-picker/day-label date) "No deadline")
                       :has? (some? date)
                       :active? (= :date current-part)
                       :doomed? (= :date doomed)
                       :on-press #(reset! !part :date)
                       :on-clear #(clear! :date)
                       :on-doom #(reset! !doomed %)}]
          [:span.half-divider]
          [draft-half {:kind :time
                       :label (if time (date-picker/format-minutes time) "Add time")
                       :has? (some? time)
                       :active? (= :time current-part)
                       :doomed? (and (some? time) (some? doomed))
                       :on-press #(reset! !part :time)
                       :on-clear #(clear! :time)
                       :on-doom #(reset! !doomed %)}]]]
        (when @!filled-today?
          [:div.head-note
           [info-icon]
           [:span "A time needs a day, so Today was filled in."]])]
       [stage
        (case current-part
          ;; A day picked is the deadline's day, and the picker's done. The
          ;; picked day pressed again keeps it — the ✕ is what clears it.
          :date [date-picker/calendar {:value date
                                       :on-pick (fn [picked]
                                                  (when picked
                                                    (pick-date! picked))
                                                  (finish!))}]
          :time [date-picker/time-view {:value time :on-change set-time! :auto-focus? true}])]])
    (finally
      (js/clearTimeout @!flash-timer)
      ;; Closed from outside: the draft stands.
      (when (and (not @!done?) (not= @!draft deadline))
        (on-save @!draft)))))

;; ---------------------------------------------------------------------------
;; Two ways to draw it
;; ---------------------------------------------------------------------------

;; Covers the nearest positioned box the form sits in — the whole card — and
;; rises from its bottom edge over a scrim. It rounds and clips itself to the
;; card's corners: 16px unless the card says otherwise through
;; `--tasks-card-radius`.
(o/defstyled sheet-frame :div
  {:position "absolute" :inset "0" :z-index "20"
   :display "flex" :flex-direction "column" :justify-content "flex-end"
   :overflow "hidden"
   :border-radius "var(--tasks-card-radius, 16px)"}
  [:.sheet-scrim
   {:position "absolute" :inset "0"
    :background "rgba(45,62,42,0.30)"
    :animation "deadline-scrim-in 0.2s ease"}]
  [:.sheet-body
   {:position "relative"
    :max-height "100%"
    :overflow-y "auto"
    :border-radius "16px 16px 0 0"
    :box-shadow "0 -8px 32px rgba(45,62,42,0.18)"
    :animation "deadline-sheet-in 0.24s ease"}])

(defn sheet
  "The picker as a sheet over the card, while `controls` has it open on a
   view; pressing the scrim closes it. `on-change` gets the saved deadline."
  [{:keys [controls deadline on-change]}]
  (let [!open (:!open controls)
        close! #(reset! !open nil)]
    (when-let [part @!open]
      [sheet-frame
       [:div.sheet-scrim {:on-click close!}]
       [:div.sheet-body
        [picker {:deadline deadline
                 :part part
                 :on-save (fn [saved]
                            (on-change saved)
                            (close!))
                 :on-cancel close!}]]])))

(defn popover
  "The [[chip]], with the picker hanging off it as a popover while `controls`
   has it open on a view; pressing outside it closes it. `on-change` gets the
   saved deadline."
  [{:keys [controls deadline on-change]}]
  (let [!open (:!open controls)
        part @!open
        close! #(reset! !open nil)]
    [c/PopoverRoot {:open (some? part)
                    :onOpenChange (fn [open?]
                                    (when-not open?
                                      (close!)))}
     [c/PopoverTrigger
      [:span {:style {:display "inline-flex"}}
       [chip {:deadline deadline :controls controls}]]]
     [c/PopoverContent {:side "bottom" :align "start" :sideOffset 6
                        :style {:width "322px" :padding "0" :overflow "hidden"}
                        ;; The picker places the caret itself.
                        :onOpenAutoFocus #(.preventDefault %)
                        :onCloseAutoFocus #(.preventDefault %)}
      (when part
        ^{:key part}
        [picker {:deadline deadline
                 :part part
                 :on-save (fn [saved]
                            (on-change saved)
                            (close!))
                 :on-cancel close!}])]]))
